import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, Loader2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [debugToken, setDebugToken] = useState("");

  const request = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setDebugToken(data.reset_token_debug || "");
      setToken(data.reset_token_debug || "");
      toast.success("Reset link generated");
      setStep(2);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setLoading(false);
  };

  const reset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset — please sign in");
      window.location.href = "/login";
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-8 shadow-lg">
        <Link to="/" className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded bg-[#0F2942] flex items-center justify-center"><Building2 className="w-5 h-5 text-white" /></div>
          <div className="font-heading font-semibold">PULB DRMS</div>
        </Link>
        <h1 className="text-2xl font-heading font-bold" data-testid="forgot-heading">Reset your password</h1>
        {step === 1 ? (
          <form onSubmit={request} className="mt-6 space-y-4">
            <div>
              <Label>Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#0F2942]" data-testid="btn-forgot-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
            </Button>
          </form>
        ) : (
          <form onSubmit={reset} className="mt-6 space-y-4">
            <div>
              <Label>Reset token {debugToken && <span className="text-xs text-slate-500">(auto-filled for demo)</span>}</Label>
              <Input required value={token} onChange={(e) => setToken(e.target.value)} data-testid="reset-token" />
            </div>
            <div>
              <Label>New password</Label>
              <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#0F2942]" data-testid="btn-reset-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset password"}
            </Button>
          </form>
        )}
        <p className="mt-6 text-sm text-center text-slate-500">
          <Link to="/login" className="text-[#0F2942] dark:text-slate-200 font-semibold hover:underline">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
